// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function QRBarcodeScannerTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="QR & Barcode Scanner App"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “QR &
        Barcode Scanner App” (the “App”). By downloading, installing, accessing,
        or using the App, you agree to be bound by these Terms. If you do not
        agree, you must uninstall the App and discontinue all use. Where
        required by platform rules or local law, additional terms may apply; in
        case of conflict with App Store or Google Play terms, the platform terms
        may prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        The App provides tools to scan QR codes and barcodes using your camera
        or supported images, display and act on recognized content, keep a
        history of scans, and generate QR codes for certain data types (such as
        URLs, text, Wi-Fi credentials, or contact details). The App may offer
        both a free tier (with basic scanning and limited history) and a
        subscription tier (“PRO”) that unlocks additional features such as
        unlimited history, QR generation, and scanning from photos. The App
        itself does not host or control the content of external websites,
        services, or files that scanned codes may link to.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Scanning, Camera Access, and Permissions</div>
      <p>
        To scan codes from the real world, the App may request access to your
        device camera. To scan from images, the App may request access to your
        photo library or files. You can choose whether to grant or revoke these
        permissions in your device settings. If you deny permissions, some
        features may be unavailable, but the App will otherwise continue to
        function where possible.
      </p>
      <p>
        When you scan a code, the App attempts to detect and decode its content
        (for example, URLs, text, Wi-Fi credentials, contact records, product
        codes). Recognition accuracy may vary based on lighting, camera quality,
        code quality, and library limitations. You are responsible for checking
        the decoded content before acting on it (for example, before opening a
        link or connecting to a network).
      </p>

      <div className={s.title}>5) History and Data Storage</div>
      <p>
        The App may automatically save a log of scanned codes and related
        metadata (for example, timestamp and decoded content) as your scan
        history. In the free version, history may be limited to a small number
        of recent items (for example, the last 10 scans). In the PRO version,
        history may be retained without a fixed limit, subject to reasonable
        technical constraints.
      </p>
      <p>
        History may be stored locally on your device and/or in cloud storage
        associated with your account, if such functionality is provided (for
        example, via Firebase). Deleting the App or clearing its data may remove
        locally stored history. You are responsible for any exports or backups
        you require. We do not guarantee long-term retention of history and may
        delete old entries to manage storage or comply with legal obligations.
      </p>

      <div className={s.title}>6) QR Code Generation and Your Responsibility</div>
      <p>
        PRO users may be able to generate QR codes based on data they enter
        (such as URLs, text, Wi-Fi credentials, or contact details) and to apply
        limited customizations (such as color or a small logo). You are solely
        responsible for ensuring that the content embedded in generated codes is
        accurate, lawful, and appropriate to share. Generated codes may be
        saved, exported, or shared through other apps and platforms; those uses
        are outside our control.
      </p>
      <p>
        You must not use generated codes to distribute malware, phishing links,
        deceptive content, or any material that violates applicable laws or
        third-party rights. You must comply with platform guidelines (for
        example, the rules of social networks or marketplaces) where you share
        QR codes.
      </p>

      <div className={s.title}>7) Subscriptions, Trials, and Billing</div>
      <p>
        Certain features of the App may be available only through paid
        auto-renewable subscriptions or other in-app purchases (“Subscriptions”)
        such as weekly, monthly, or yearly plans. PRO features may include
        unlimited scan history, QR generation, scanning from images, and other
        capabilities described in the App or on the paywall screen. Exact
        pricing, plan names, term lengths, and availability may vary by region
        and may be updated over time.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are determined by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>8) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to PRO features.
        An internet connection may be required to validate or refresh
        entitlements after long offline periods. If platform billing reverses,
        refunds, or flags a transaction as fraudulent, related entitlements may
        be limited, suspended, or revoked.
      </p>

      <div className={s.title}>9) External Content and Links</div>
      <p>
        Many QR codes and barcodes contain links or references to external
        websites, apps, networks, or content that we do not operate or control.
        We are not responsible for the availability, security, legality, or
        quality of external content, products, or services accessed through
        scanned codes. You should review URLs and other decoded data before
        opening them, particularly if they appear suspicious, unfamiliar, or
        shortened. Use up-to-date security measures (such as antivirus software
        and OS updates) and be cautious when entering personal or payment
        information on third-party sites.
      </p>

      <div className={s.title}>10) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful purposes and not to:
      </p>
      <ul>
        <li>
          scan or generate codes for the purpose of distributing malware,
          phishing, or fraudulent schemes;
        </li>
        <li>
          embed content that is illegal, abusive, hateful, or infringes
          intellectual property, privacy, or other rights;
        </li>
        <li>
          reverse engineer, decompile, or attempt to extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          use automated tools to overload, scrape, or misuse the App or its
          backend services;
        </li>
        <li>
          circumvent technical protections, usage limits, or paywalls implemented
          to separate free and PRO features.
        </li>
      </ul>

      <div className={s.title}>11) Intellectual Property</div>
      <p>
        You retain ownership of any original data you input to generate QR codes
        and the content of scanned codes that you own. All software, interfaces,
        designs, trademarks, logos, and other materials provided with the App
        (excluding your content and third-party content) are owned by the
        publisher or its licensors and are protected by intellectual property
        laws. Subject to these Terms and any Subscription limitations, you are
        granted a limited, non-exclusive, non-transferable, revocable license to
        use the App and its features solely for your personal or permitted
        business purposes.
      </p>

      <div className={s.title}>12) Third-Party Services and Libraries</div>
      <p>
        The App may use third-party services and libraries for scanning,
        generating codes, storage, analytics, crash reporting, and subscription
        management. Examples include barcode and QR scanning libraries, QR
        generation libraries, Firebase (for authentication, storage, analytics,
        and crash reporting), and RevenueCat (for subscriptions). Technical data
        and usage information may be shared with these providers to operate and
        improve the App. These providers act under their own terms and privacy
        policies, and we require them by contract to handle data appropriately
        and only for allowed purposes.
      </p>

      <div className={s.title}>13) Analytics and Performance</div>
      <p>
        We may use analytics tools (for example, Firebase Analytics) to
        understand how the App is used and to improve reliability, performance,
        and feature design. Typical events may include screen views, successful
        or failed scans, history usage, QR generation actions, paywall views,
        and purchases. Aggregated and anonymized statistics may be used to guide
        product decisions. Where required by law, we will obtain necessary
        permissions or provide settings to manage analytics preferences.
      </p>

      <div className={s.title}>14) No Warranties and Service Changes</div>
      <p>
        We aim to provide fast and accurate scanning, reliable history, and
        robust QR generation, but we do not guarantee that all codes will be
        recognized, that history will never be lost, or that generated codes
        will be readable by all devices and apps. The App and its features may
        change, be updated, or be removed over time. We may add, modify, or
        discontinue free or PRO features as part of ongoing development, subject
        to applicable consumer-protection laws.
      </p>

      <div className={s.title}>15) Limitation of Liability</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and
        non-infringement. To the extent allowed by law, we are not liable for
        indirect, incidental, consequential, or punitive damages, loss of
        profits, data loss (including loss of scan history or generated codes),
        or business interruption arising from or related to your use of or
        inability to use the App. Some jurisdictions do not allow certain
        limitations or exclusions, so parts of this section may not apply to
        you.
      </p>

      <div className={s.title}>16) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your content, your breach of these
        Terms, or your violation of any rights of a third party (including the
        distribution or use of harmful or unlawful codes).
      </p>

      <div className={s.title}>17) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>18) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        QR &amp; Barcode Scanner App, which explains how information is
        collected, used, stored, and shared, including scanning history and
        analytics. In case of conflict between these Terms and the Privacy
        Policy regarding data processing, the Privacy Policy will guide how we
        handle personal data, while these Terms govern your use of the App and
        its features. You should review the Privacy Policy regularly to
        understand current practices.
      </p>

      <div className={s.title}>19) Changes to These Terms</div>
      <p>
        We may update the App, add or remove features (including free and PRO
        capabilities), or modify these Terms from time to time. The “Last
        updated” date at the top of this document indicates when these Terms
        were last revised. Material changes may be highlighted in the App or via
        other reasonable means. By continuing to use the App after changes take
        effect, you accept the updated Terms. If you do not agree to the revised
        Terms, you must stop using the App and uninstall it.
      </p>

      <div className={s.title}>20) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account-specific
        or purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
